import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Sun, Moon, MapPin } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";

const REASONS = [
  { v: "bilan", t: "Bilan auditif" },
  { v: "premier", t: "Premier rendez-vous" },
  { v: "appareillage", t: "Appareillage" },
  { v: "controle", t: "Contrôle ou suivi" },
  { v: "reglage", t: "Réglage ou entretien" },
  { v: "autre", t: "Autre demande" },
];

const STEPS = [
  "Réseau", "Ville ou code postal", "Centre", "Motif", "Date", "Matin ou après-midi", "Coordonnées",
];

export default function Appointment() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [networks, setNetworks] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loadingCenters, setLoadingCenters] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    network_slug: params.get("network") || "",
    custom_network: "",
    city: "",
    postal_code: "",
    center_id: params.get("center") || "",
    reason: "",
    preferred_date: "",
    time_preference: "",
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    consent_data_share: false,
    consent_marketing: false,
  });

  useEffect(() => {
    api.get("/networks").then((r) => setNetworks(r.data.networks || []));
  }, []);

  useEffect(() => {
    if (!form.network_slug) return;
    setLoadingCenters(true);
    api
      .get("/centers", { params: { network: form.network_slug, city: form.city || undefined, postal_code: form.postal_code || undefined, limit: 50 } })
      .then((r) => setCenters(r.data.centers || []))
      .finally(() => setLoadingCenters(false));
  }, [form.network_slug, form.city, form.postal_code]);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        first_name: f.first_name || user.first_name || "",
        last_name: f.last_name || user.last_name || "",
        email: f.email || user.email || "",
        phone: f.phone || user.phone || "",
      }));
    }
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validStep = useMemo(() => {
    switch (step) {
      case 0:
        if (form.network_slug === "autre") return !!(form.custom_network || "").trim();
        return !!form.network_slug;
      case 1: return !!(form.city || form.postal_code);
      case 2: return form.network_slug === "autre" ? true : !!form.center_id;
      case 3: return !!form.reason;
      case 4: return !!form.preferred_date;
      case 5: return !!form.time_preference;
      case 6:
        return (
          !!form.first_name && !!form.last_name && !!form.email &&
          !!form.phone && !!form.consent_data_share
        );
      default: return false;
    }
  }, [step, form]);

  const next = () => {
    if (!validStep) return toast.error("Veuillez compléter cette étape");
    setStep((s) => Math.min(6, s + 1));
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!validStep) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/appointments", form);
      toast.success(`Demande enregistrée · Référence ${data.reference}`);
      nav(user ? "/espace-client" : `/?ref=${data.reference}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="hero-bg min-h-[calc(100vh-5rem)] py-14">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="mb-8">
          <div className="text-gold text-sm uppercase tracking-widest font-semibold">Prendre rendez-vous</div>
          <h1 className="font-heading text-3xl md:text-4xl text-primary-blue mt-2">
            Réservez votre rendez-vous <span className="text-gold">gratuit</span>
          </h1>
          <div className="mt-6 flex items-center justify-between text-sm text-slate-600">
            <span data-testid="wizard-step-label">Étape {step + 1} sur {STEPS.length} · {STEPS[step]}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="mt-2 h-2" />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 card-lux p-8 md:p-10">
          {step === 0 && (
            <div>
              <h2 className="font-heading text-2xl text-primary-blue">Choisissez un réseau</h2>
              <p className="text-slate-600 mt-2">Sélectionnez l'un de nos partenaires officiels ou choisissez « Autre ».</p>
              <div className="mt-6 grid md:grid-cols-3 gap-4">
                {networks.map((n) => {
                  const active = form.network_slug === n.slug;
                  return (
                    <button
                      key={n.slug}
                      onClick={() => set("network_slug", n.slug)}
                      data-testid={`wizard-network-${n.slug}`}
                      className={`text-left p-6 rounded-xl border-2 transition-all ${active ? "border-primary-blue bg-sky-blue" : "border-slate-200 hover:border-primary-blue/40"}`}
                    >
                      <div className="font-heading text-xl text-primary-blue">{n.name}</div>
                      <div className="text-sm text-slate-600 mt-2 line-clamp-3">{n.description}</div>
                    </button>
                  );
                })}
                <button
                  onClick={() => set("network_slug", "autre")}
                  data-testid="wizard-network-autre"
                  className={`text-left p-6 rounded-xl border-2 transition-all ${form.network_slug === "autre" ? "border-primary-blue bg-sky-blue" : "border-slate-200 hover:border-primary-blue/40"}`}
                >
                  <div className="font-heading text-xl text-primary-blue">Autre</div>
                  <div className="text-sm text-slate-600 mt-2">Un autre réseau ou un centre spécifique.</div>
                </button>
              </div>
              {form.network_slug === "autre" && (
                <div className="mt-6">
                  <Label htmlFor="custom-net" className="text-base">Précisez le réseau ou centre recherché <span className="text-red-600">*</span></Label>
                  <Input
                    id="custom-net"
                    data-testid="wizard-custom-network"
                    value={form.custom_network || ""}
                    onChange={(e) => set("custom_network", e.target.value)}
                    placeholder="Nom du réseau ou du centre"
                    className="mt-2 h-12 text-lg"
                  />
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div>
              <h2 className="font-heading text-2xl text-primary-blue">Où souhaitez-vous être reçu ?</h2>
              <p className="text-slate-600 mt-2">Indiquez au moins une ville ou un code postal.</p>
              <div className="mt-6 grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="city" className="text-base">Ville</Label>
                  <Input id="city" data-testid="wizard-city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Ex : Paris" className="mt-2 h-12 text-lg" />
                </div>
                <div>
                  <Label htmlFor="pc" className="text-base">Code postal</Label>
                  <Input id="pc" data-testid="wizard-postal" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} placeholder="Ex : 75015" className="mt-2 h-12 text-lg" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="font-heading text-2xl text-primary-blue">Choisissez un centre</h2>
              <p className="text-slate-600 mt-2">Voici les centres correspondants à votre recherche.</p>
              <div className="mt-6 space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {loadingCenters && <div className="text-slate-500">Chargement…</div>}
                {!loadingCenters && centers.length === 0 && (
                  <div className="text-slate-500 p-4 bg-slate-50 rounded-lg">Aucun centre trouvé. Élargissez votre recherche.</div>
                )}
                {centers.map((c) => {
                  const active = form.center_id === c.id;
                  return (
                    <button
                      key={c.id}
                      onClick={() => set("center_id", c.id)}
                      data-testid={`wizard-center-${c.id}`}
                      className={`w-full text-left p-5 rounded-xl border-2 transition-all ${active ? "border-primary-blue bg-sky-blue" : "border-slate-200 hover:border-primary-blue/40"}`}
                    >
                      <div className="font-semibold text-lg text-primary-blue">{c.name}</div>
                      <div className="text-sm text-slate-600 mt-1 flex items-center gap-1"><MapPin size={14} /> {c.address}, {c.postal_code} {c.city}</div>
                      {c.phone && <div className="text-sm text-slate-500 mt-1">Tél : {c.phone}</div>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="font-heading text-2xl text-primary-blue">Quel est le motif ?</h2>
              <div className="mt-6 grid md:grid-cols-2 gap-3">
                {REASONS.map((r) => {
                  const active = form.reason === r.v;
                  return (
                    <button
                      key={r.v}
                      onClick={() => set("reason", r.v)}
                      data-testid={`wizard-reason-${r.v}`}
                      className={`p-5 rounded-xl border-2 text-left font-medium text-lg transition ${active ? "border-primary-blue bg-sky-blue text-primary-blue" : "border-slate-200 hover:border-primary-blue/40 text-slate-800"}`}
                    >
                      {r.t}
                    </button>
                  );
                })}
              </div>
              <p className="text-sm text-slate-500 mt-4">Aucune information médicale détaillée n'est demandée.</p>
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="font-heading text-2xl text-primary-blue">Date souhaitée</h2>
              <p className="text-slate-600 mt-2">Choisissez une date préférée. Elle sera à valider avec le centre.</p>
              <div className="mt-6 max-w-sm">
                <Label htmlFor="date" className="text-base">Date</Label>
                <Input
                  id="date"
                  type="date"
                  data-testid="wizard-date"
                  value={form.preferred_date}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => set("preferred_date", e.target.value)}
                  className="mt-2 h-12 text-lg"
                />
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <h2 className="font-heading text-2xl text-primary-blue">Matin ou après-midi ?</h2>
              <div className="mt-6 grid md:grid-cols-2 gap-4">
                {[{ v: "morning", t: "Matin", i: Sun }, { v: "afternoon", t: "Après-midi", i: Moon }].map((o) => {
                  const Icon = o.i;
                  const active = form.time_preference === o.v;
                  return (
                    <button
                      key={o.v}
                      onClick={() => set("time_preference", o.v)}
                      data-testid={`wizard-time-${o.v}`}
                      className={`p-8 rounded-xl border-2 flex flex-col items-center gap-3 transition ${active ? "border-primary-blue bg-sky-blue" : "border-slate-200 hover:border-primary-blue/40"}`}
                    >
                      <Icon size={32} className={active ? "text-primary-blue" : "text-slate-500"} />
                      <span className="font-heading text-xl text-primary-blue">{o.t}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 6 && (
            <div>
              <h2 className="font-heading text-2xl text-primary-blue">Vos coordonnées</h2>
              <p className="text-slate-600 mt-2">Ces informations seront transmises uniquement au centre sélectionné.</p>
              <div className="mt-6 grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="fn" className="text-base">Prénom</Label>
                  <Input id="fn" data-testid="wizard-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-2 h-12 text-lg" />
                </div>
                <div>
                  <Label htmlFor="ln" className="text-base">Nom</Label>
                  <Input id="ln" data-testid="wizard-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-2 h-12 text-lg" />
                </div>
                <div>
                  <Label htmlFor="em" className="text-base">E-mail</Label>
                  <Input id="em" type="email" data-testid="wizard-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2 h-12 text-lg" />
                </div>
                <div>
                  <Label htmlFor="ph" className="text-base">Téléphone</Label>
                  <Input id="ph" data-testid="wizard-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 h-12 text-lg" />
                </div>
              </div>

              <div className="mt-6 space-y-4 p-5 bg-sky-blue/40 rounded-xl border border-primary-blue/10">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={form.consent_data_share}
                    onCheckedChange={(v) => set("consent_data_share", !!v)}
                    data-testid="wizard-consent-data"
                    className="mt-1"
                  />
                  <span className="text-slate-800">
                    J'accepte que mes données soient transmises <strong>uniquement</strong> au centre auditif sélectionné,
                    dans le but d'organiser mon rendez-vous. <span className="text-red-600">*</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={form.consent_marketing}
                    onCheckedChange={(v) => set("consent_marketing", !!v)}
                    data-testid="wizard-consent-marketing"
                    className="mt-1"
                  />
                  <span className="text-slate-800">
                    J'accepte de recevoir des conseils et informations d'Audition Expert (facultatif).
                  </span>
                </label>
              </div>

              <div className="mt-4 p-4 bg-slate-50 rounded-lg text-sm text-slate-600 flex gap-2">
                <CheckCircle2 size={18} className="text-primary-blue shrink-0 mt-0.5" />
                Le rendez-vous ne sera confirmé qu'après validation par le centre puis par notre équipe.
              </div>
            </div>
          )}

          <div className="mt-10 flex items-center justify-between">
            <Button
              variant="outline"
              onClick={prev}
              disabled={step === 0}
              className="border-2"
              data-testid="wizard-prev"
            >
              <ArrowLeft size={18} className="mr-2" /> Précédent
            </Button>
            {step < 6 ? (
              <Button
                onClick={next}
                disabled={!validStep}
                className="bg-primary-blue hover:bg-[#0a1d36] text-white"
                data-testid="wizard-next"
              >
                Continuer <ArrowRight size={18} className="ml-2" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={!validStep || submitting}
                className="bg-primary-blue hover:bg-[#0a1d36] text-white"
                data-testid="wizard-submit"
              >
                {submitting ? "Envoi…" : "Envoyer ma demande"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
